import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatNativeDateModule } from '@angular/material/core';
import { NgxCurrencyDirective } from 'ngx-currency';
import { forkJoin } from 'rxjs';
import { MaskService } from '../../services/mask.service';
import { AlertService, AlertType } from '../../services/alert.service';
import { ControleEstoqueService } from '../../services/apis/produto/controle-estoque.service';
import { ControleEstoqueDetalheLotesRequest } from '../../services/requests/produto/controle-estoque-detalhe-lotes.request';

export interface LoteInventarioModalData {
  idInventarioItem?: number;
  idProduto: number;
  idLocalizacaoEstoque: number;
  nomeProduto: string;
  skuProduto?: string;
  saldoSistema: number;
  lotes: LoteInventarioModalItem[];
  isReadOnly?: boolean;
}

export interface LoteInventarioModalItem {
  idInventarioItemLote?: number;
  idLote?: number;
  codigoLote: string;
  dataFabricacao?: string;
  validadeLote?: string;
  saldoSistema: number;
  saldoContado: number;
  isExistente?: boolean;
  isRemovido?: boolean;
}

interface LoteInvEditRow {
  idInventarioItemLote?: number;
  idLote?: number;
  codigoLote: string;
  dataFabricacao: Date | null;
  validadeLote: Date | null;
  saldoSistema: number;
  saldoContado: number;
  isExistente?: boolean;
  isRemovido?: boolean;
}

@Component({
  selector: 'app-lote-inventario-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    NgxCurrencyDirective,
  ],
  templateUrl: './lote-inventario-modal.component.html',
  styleUrl: './lote-inventario-modal.component.css',
})
export class LoteInventarioModalComponent implements OnInit {
  public nomeProduto: string = '';
  public skuProduto: string = '';
  public saldoSistema: number = 0;
  public isReadOnly: boolean = false;
  public isLoading: boolean = false;
  public erroCarga: string | null = null;

  public dataSource = new MatTableDataSource<LoteInvEditRow>([]);
  public displayedColumns: string[] = [
    'codigoLote',
    'dataFabricacao',
    'validadeLote',
    'saldoSistema',
    'saldoContado',
    'acoes',
  ];

  public quantidadeMask = this.maskService.getDynamicDecimalMask();
  public readonly hoje: Date = new Date();

  public isNarrow = window.innerWidth <= 700;
  @HostListener('window:resize') onResize(): void {
    this.isNarrow = window.innerWidth <= 700;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LoteInventarioModalData,
    public dialogRef: MatDialogRef<LoteInventarioModalComponent>,
    private controleEstoqueService: ControleEstoqueService,
    private maskService: MaskService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.nomeProduto = this.data.nomeProduto;
    this.skuProduto = this.data.skuProduto ?? '';
    this.saldoSistema = this.data.saldoSistema;
    this.isReadOnly = this.data.isReadOnly ?? false;

    if (this.isReadOnly) {
      this.displayedColumns = [
        'codigoLote',
        'dataFabricacao',
        'validadeLote',
        'saldoSistema',
        'saldoContado',
      ];
    }

    this.carregarLotesExistentes();
  }

  private carregarLotesExistentes(): void {
    this.isLoading = true;
    this.erroCarga = null;

    const request: ControleEstoqueDetalheLotesRequest = {
      idLocalizacaoEstoque: this.data.idLocalizacaoEstoque,
      idSituacaoFiltro: 0,
    };

    this.controleEstoqueService.getLotes(this.data.idProduto, request, 0).subscribe({
      next: (resp) => {
        const existentes: LoteInvEditRow[] = (resp?.list ?? [])
          .filter(l => (l.saldoDisponivel ?? 0) > 0)
          .map(l => ({
            idLote: l.idLote,
            codigoLote: l.numeroLote,
            dataFabricacao: this.parseDate(l.dataFabricacao),
            validadeLote: this.parseDate(l.dataValidade),
            saldoSistema: l.saldoDisponivel,
            saldoContado: l.saldoDisponivel,
            isExistente: true,
          }));

        const salvosPorIdLote = new Map<number, LoteInventarioModalItem>();
        const salvosSemIdLote: LoteInventarioModalItem[] = [];
        for (const s of this.data.lotes ?? []) {
          if (s.idLote && s.idLote > 0) salvosPorIdLote.set(s.idLote, s);
          else salvosSemIdLote.push(s);
        }

        for (const linha of existentes) {
          if (linha.idLote != null && salvosPorIdLote.has(linha.idLote)) {
            const salvo = salvosPorIdLote.get(linha.idLote)!;
            linha.idInventarioItemLote = salvo.idInventarioItemLote;
            linha.saldoContado = salvo.saldoContado;
            if (salvo.saldoContado === 0) linha.isRemovido = true;
            salvosPorIdLote.delete(linha.idLote);
          }
        }

        const orfaos: LoteInvEditRow[] = [];
        salvosPorIdLote.forEach(s => orfaos.push(this.toEditRow(s, false)));
        for (const s of salvosSemIdLote) {
          orfaos.push(this.toEditRow(s, false));
        }

        this.dataSource.data = [...existentes, ...orfaos];
        this.isLoading = false;
      },
      error: () => {
        this.erroCarga = 'Não foi possível carregar lotes existentes. Apenas inclusão manual disponível.';
        this.dataSource.data = (this.data.lotes ?? []).map(l =>
          this.toEditRow(l, !!l.idLote && l.idLote > 0));
        this.isLoading = false;
      },
    });
  }

  public get totalContado(): number {
    return this.dataSource.data.reduce((sum, l) => sum + (l.saldoContado || 0), 0);
  }

  public isLinhaInvalida(row: LoteInvEditRow): boolean {
    if (row.isExistente || row.isRemovido) return false;
    const codigo = (row.codigoLote || '').trim();
    if (!codigo) return true;
    const duplicados = this.dataSource.data.filter(
      l => (l.codigoLote || '').trim().toLowerCase() === codigo.toLowerCase());
    return duplicados.length > 1;
  }

  public get hasLinhaInvalida(): boolean {
    return this.dataSource.data.some(r => this.isLinhaInvalida(r));
  }

  public adicionarLinha(): void {
    this.dataSource.data = [...this.dataSource.data, {
      codigoLote: '',
      dataFabricacao: null,
      validadeLote: null,
      saldoSistema: 0,
      saldoContado: 0,
      isExistente: false,
    }];
  }

  public removerLote(index: number): void {
    const lista = [...this.dataSource.data];
    const row = lista[index];
    if (row?.isExistente) {
      row.saldoContado = 0;
      row.isRemovido = true;
      this.dataSource.data = lista;
    } else {
      lista.splice(index, 1);
      this.dataSource.data = lista;
    }
  }

  public desfazerRemocao(index: number): void {
    const lista = [...this.dataSource.data];
    const row = lista[index];
    if (row?.isExistente && row.isRemovido) {
      row.saldoContado = row.saldoSistema;
      row.isRemovido = false;
      this.dataSource.data = lista;
    }
  }

  public isSalvando = false;

  public confirmar(): void {
    if (this.hasLinhaInvalida || this.isSalvando) return;

    // Lote digitado novo (sem id): cria/obtém na API Produto AGORA — daí em diante só o id
    // trafega (Suprimentos não guarda mais snapshot de código/datas).
    const novos = this.dataSource.data.filter(r => !r.idLote);
    if (novos.length === 0) {
      this.fechar();
      return;
    }

    this.isSalvando = true;
    const chamadas = novos.map(r =>
      this.controleEstoqueService.obterOuCriarLote({
        idProduto: this.data.idProduto,
        codigoLote: r.codigoLote.trim(),
        dataValidade: this.toIso(r.validadeLote),
        dataFabricacao: this.toIso(r.dataFabricacao),
      }));

    forkJoin(chamadas).subscribe({
      next: respostas => {
        this.isSalvando = false;
        const falha = respostas.find(resp => resp?.isError || !resp?.idLote);
        if (falha) {
          this.alertService.show('Erro', falha.errorDescription || 'Não foi possível registrar o lote informado.', AlertType.error);
          return; // mantém a digitação do usuário no modal
        }
        respostas.forEach((resp, i) => (novos[i].idLote = resp.idLote));
        this.fechar();
      },
      error: () => {
        this.isSalvando = false;
        this.alertService.show('Erro', 'Não foi possível registrar os lotes informados.', AlertType.error);
      },
    });
  }

  private fechar(): void {
    const lotes: LoteInventarioModalItem[] = this.dataSource.data.map(r => ({
      idInventarioItemLote: r.idInventarioItemLote,
      idLote: r.idLote,
      codigoLote: r.codigoLote.trim(),
      dataFabricacao: this.toIso(r.dataFabricacao),
      validadeLote: this.toIso(r.validadeLote),
      saldoSistema: r.saldoSistema,
      saldoContado: r.saldoContado || 0,
      isExistente: r.isExistente,
      isRemovido: r.isRemovido,
    }));
    this.dialogRef.close({ confirmed: true, lotes });
  }

  public cancelar(): void {
    this.dialogRef.close({ confirmed: false });
  }

  public onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  public onDateKeydown(event: KeyboardEvent): void {
    const teclasPermitidas = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    const isAtalho = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
    if (teclasPermitidas.includes(event.key) || isAtalho) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  public onDataFabricacaoBlur(event: FocusEvent, row: LoteInvEditRow): void {
    const input = event.target as HTMLInputElement;
    const data = this.lerDataInput(input);
    if (data && data > this.hojeNormalizado()) {
      input.value = '';
      row.dataFabricacao = null;
      this.alertService.show('Atenção', 'A data de fabricação não pode ser maior que a data atual.', AlertType.warning);
      return;
    }
    if (data && row.validadeLote && data > row.validadeLote) {
      input.value = '';
      row.dataFabricacao = null;
      this.alertService.show('Atenção', 'A data de fabricação não pode ser maior que a data de validade.', AlertType.warning);
      return;
    }
    row.dataFabricacao = data;
  }

  public onDataValidadeBlur(event: FocusEvent, row: LoteInvEditRow): void {
    const input = event.target as HTMLInputElement;
    const data = this.lerDataInput(input);
    if (data && row.dataFabricacao && data < row.dataFabricacao) {
      input.value = '';
      row.validadeLote = null;
      this.alertService.show('Atenção', 'A data de validade não pode ser menor que a data de fabricação.', AlertType.warning);
      return;
    }
    row.validadeLote = data;
  }

  private lerDataInput(input: HTMLInputElement): Date | null {
    const valor = (input?.value || '').trim();
    if (!valor) return null;
    const apenasNumeros = valor.replace(/\D/g, '').substring(0, 8);
    if (apenasNumeros.length !== 8) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
      return null;
    }
    const formatado = `${apenasNumeros.substring(0, 2)}/${apenasNumeros.substring(2, 4)}/${apenasNumeros.substring(4, 8)}`;
    input.value = formatado;
    const data = this.parseDataBr(formatado);
    if (!data) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
    }
    return data;
  }

  private parseDataBr(valor: string): Date | null {
    const partes = valor.split('/');
    if (partes.length !== 3) return null;
    const dia = Number(partes[0]);
    const mes = Number(partes[1]);
    const ano = Number(partes[2]);
    if (!Number.isInteger(dia) || !Number.isInteger(mes) || !Number.isInteger(ano)) return null;
    if (ano < 1000 || ano > 9999 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
    const data = new Date(ano, mes - 1, dia);
    data.setHours(0, 0, 0, 0);
    if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;
    return data;
  }

  private hojeNormalizado(): Date {
    const h = new Date();
    h.setHours(0, 0, 0, 0);
    return h;
  }

  private toEditRow(s: LoteInventarioModalItem, isExistente: boolean): LoteInvEditRow {
    return {
      idInventarioItemLote: s.idInventarioItemLote,
      idLote: s.idLote,
      codigoLote: s.codigoLote,
      dataFabricacao: this.parseDate(s.dataFabricacao),
      validadeLote: this.parseDate(s.validadeLote),
      saldoSistema: isExistente ? s.saldoSistema : 0,
      saldoContado: s.saldoContado,
      isExistente,
      isRemovido: s.isRemovido,
    };
  }

  private parseDate(s?: string): Date | null {
    if (!s) return null;
    const dateOnly = s.substring(0, 10);
    const [year, month, day] = dateOnly.split('-').map(Number);
    if (!year || !month || !day) return null;
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private toIso(d: Date | null): string | undefined {
    if (!d || Number.isNaN(d.getTime())) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
